import { FormOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { AInput, AModal } from '../../../components';
import { Card, Form, Space, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useI18n } from '../../../shared/i18n';

const { Text } = Typography;

interface ITenantCreateEditProps {
  open: boolean;
  close: () => void;
  loading: boolean;
  existingTenants: string[];
  onCreate: (name: string) => Promise<void>;
}

const TenantCreateEdit: React.FC<ITenantCreateEditProps> = ({
  open,
  close,
  loading,
  existingTenants,
  onCreate,
}) => {
  const { t } = useI18n();
  const [name, setName] = useState<string>('');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!open) {
      return;
    }

    setName('');
    setError('');
  }, [open]);

  const handleCreate = async () => {
    const normalizedName = name.trim();

    if (!normalizedName) {
      setError(t('companiesPage.createTenantModal.validation.required'));
      return;
    }

    const alreadyExists = existingTenants.some(
      (item) => item.toLowerCase() === normalizedName.toLowerCase(),
    );

    if (alreadyExists) {
      setError(t('companiesPage.createTenantModal.validation.alreadyExists'));
      return;
    }

    setError('');

    try {
      await onCreate(normalizedName);
      close();
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          t('companiesPage.createTenantModal.validation.createError'),
      );
    }
  };

  return (
    <AModal
      open={open}
      close={close}
      title={
        <>
          <FormOutlined /> {t('companiesPage.createTenantModal.title')}
        </>
      }
      onOk={handleCreate}
      confirmLoading={loading}
      width={640}
    >
      <Form layout="vertical" style={{ marginTop: 8 }}>
        <Card size="small">
          <Form.Item
            label={t('companiesPage.createTenantModal.fields.identifier')}
            required
          >
            <AInput
              value={name}
              placeholder={t(
                'companiesPage.createTenantModal.fields.identifierPlaceholder',
              )}
              onChange={(e) => setName(e.target.value)}
              disabled={loading}
            />
          </Form.Item>

          <Space size={6} style={{ marginTop: 2 }}>
            <InfoCircleOutlined style={{ color: '#2f54eb' }} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('companiesPage.createTenantModal.helper')}
            </Text>
          </Space>

          {error && (
            <Text style={{ color: '#cf1322', display: 'block', marginTop: 10 }}>
              {error}
            </Text>
          )}
        </Card>
      </Form>
    </AModal>
  );
};

export default TenantCreateEdit;
